import { shallowRef, watch, onMounted, onBeforeUnmount } from "vue"

export const useSetupDocument = ({ containerState, pagingState, scalingState, documentPages }) => {
  const presentingPageNumbers = shallowRef([])

  let observer

  let _textContentCanvas = null
  const getTextContentCanvasContext = () => {
    if (!_textContentCanvas) {
      _textContentCanvas = document.createElement("canvas")
      _textContentCanvas.classList.add("hidden")
      document.body.append(_textContentCanvas)
    }

    const context = _textContentCanvas.getContext("2d", { alpha: false })

    return context
  }

  let _textContentAscentCache = new Map()
  const getTextContentAscent = fontFamily => {
    const cachedAscent = _textContentAscentCache.get(fontFamily)

    if (cachedAscent) {
      return cachedAscent
    }

    const ctx = getTextContentCanvasContext()
    const savedFont = ctx.font
    ctx.canvas.width = ctx.canvas.height = 30
    ctx.font = `30px ${fontFamily}`
    const metrics = ctx.measureText("")

    let ascent = metrics.fontBoundingBoxAscent
    let descent = Math.abs(metrics.fontBoundingBoxDescent)
    if (ascent) {
      const ratio = ascent / (ascent + descent)
      _textContentAscentCache.set(fontFamily, ratio)

      ctx.canvas.width = ctx.canvas.height = 0
      ctx.font = savedFont
      return ratio
    }

    ctx.strokeStyle = "red"
    ctx.clearRect(0, 0, 30, 30)
    ctx.strokeText("g", 0, 0)
    let pixels = ctx.getImageData(0, 0, DEFAULT_FONT_SIZE, DEFAULT_FONT_SIZE).data
    descent = 0
    for (let i = pixels.length - 1 - 3; i >= 0; i -= 4) {
      if (pixels[i] > 0) {
        descent = Math.ceil(i / 4 / 30)
        break
      }
    }

    ctx.clearRect(0, 0, 30, 30)
    ctx.strokeText("A", 0, 30)
    pixels = ctx.getImageData(0, 0, 30, 30).data
    ascent = 0
    for (let i = 0, ii = pixels.length; i < ii; i += 4) {
      if (pixels[i] > 0) {
        ascent = 30 - Math.floor(i / 4 / 30)
        break
      }
    }

    ctx.canvas.width = ctx.canvas.height = 0
    ctx.font = savedFont

    const ratio = ascent ? ascent / (ascent + descent) : 0.8
    this.textContentAscentCache.set(fontFamily, ratio)
    return ratio
  }

  watch(
    [containerState.element, documentPages],
    ([containerElement, documentPages], [oldContainerElement, oldDocumentPages]) => {
      if (!oldContainerElement && observer) {
        observer.disconnect()
        observer = undefined
      }

      if (oldDocumentPages && observer) {
        for (const page of oldDocumentPages) {
          observer.unobserve(page)
        }
      }

      if (
        !containerElement ||
        !documentPages ||
        documentPages.length === 0 ||
        typeof pagingState.currentPage.value === "undefined"
      ) {
        return
      }

      observer = new IntersectionObserver(
        entries => {
          const appeared = []
          const disappeared = []

          for (const entry of entries) {
            if (entry.isIntersecting) {
              appeared.push(parseInt(entry.target.dataset.pageNumber, 10))
            } else {
              disappeared.push(parseInt(entry.target.dataset.pageNumber, 10))
            }
          }

          const newPageNumberSet = new Set(presentingPageNumbers.value)
          appeared.forEach(pn => {
            newPageNumberSet.add(pn)
          })
          disappeared.forEach(pn => {
            newPageNumberSet.delete(pn)
          })

          presentingPageNumbers.value = Array.from(newPageNumberSet).sort((a, b) => a - b)
        },
        {
          root: containerElement
        }
      )

      setTimeout(() => {
        for (const page of documentPages) {
          observer.observe(page)
        }
      }, 500)
    },
    { immediate: true }
  )

  watch(
    presentingPageNumbers,
    presentingPageNumbers => {
      if (!presentingPageNumbers || presentingPageNumbers.length === 0) return
      pagingState.currentPage.value = presentingPageNumbers[0]
    },
    { immediate: true }
  )

  let unobserveTimeoutId
  watch(
    [containerState.width, containerState.height, scalingState.scale],
    () => {
      if (!observer) return

      if (unobserveTimeoutId) {
        clearTimeout(unobserveTimeoutId)
      }

      if (!documentPages.value) return

      for (const page of documentPages.value) {
        observer.unobserve(page)
      }

      unobserveTimeoutId = setTimeout(() => {
        if (!observer) return
        if (!documentPages.value) return

        for (const page of documentPages.value) {
          observer.observe(page)
        }
        unobserveTimeoutId = undefined
      }, 100)
    },
    { immediate: true }
  )

  const onContainerElementWheel = event => {
    if (!event.ctrlKey) return

    event.preventDefault()

    if (event.deltaY < 0) {
      scalingState.zoomInContinuous()
    } else {
      scalingState.zoomOutContinuous()
    }
  }

  watch(
    containerState.element,
    (containerElement, oldContainerElement) => {
      if (oldContainerElement) {
        oldContainerElement.removeEventListener("wheel", onContainerElementWheel)
      }

      if (!containerElement) return

      containerElement.addEventListener("wheel", onContainerElementWheel)
    },
    { immediate: true }
  )

  onBeforeUnmount(() => {
    if (_textContentCanvas) {
      _textContentCanvas.remove()
      _textContentCanvas = null
    }

    _textContentAscentCache = new Map()

    if (containerState.element.value) {
      containerState.element.value.removeEventListener("wheel", onContainerElementWheel)
    }

    if (observer) {
      observer.disconnect()
    }
  })

  return {
    presentingPageNumbers,
    getTextContentCanvasContext,
    getTextContentAscent
  }
}
